import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import timerIconSrc from 'images/icons/timer.png';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import { createTimerFormat } from '../../../utils/purchase/timer';

/**
 * Renders the quick purchase timer badge.
 * @param {object} props - Component props.
 * @param {number} props.remainingTime - Remaining purchase time in seconds.
 * @returns {JSX.Element|null} The timer badge or null when hidden.
 */
const QuickPurchaseTimerBadge = ({ remainingTime }) => {
  const { PURCHASE_TIMER_ENABLED } = useWhitelabelFeatures();
  const { t } = useTranslation('purchase');
  const remainingSeconds = Math.ceil(remainingTime || 0);

  if (!PURCHASE_TIMER_ENABLED || remainingSeconds <= 0) return null;

  const timeDisplay = createTimerFormat(remainingSeconds, 'mm:ss');

  return (
    <div
      className="fixed left-1/2 top-[max(16px,calc(env(safe-area-inset-top)+16px))]
        z-[1000] flex min-h-11 w-[calc(100vw-72px)] max-w-[380px]
        -translate-x-1/2 items-center justify-center gap-1.5 whitespace-nowrap rounded-full
        border border-[var(--gray-200)] bg-white px-4 py-2 text-sm font-bold leading-none
        text-[var(--gray-600)] shadow-[0_6px_18px_rgba(0,0,0,0.16)]
        max-[359px]:min-h-10 max-[359px]:w-[calc(100vw-40px)] max-[359px]:gap-1
        max-[359px]:px-3 max-[359px]:py-1.5 max-[359px]:text-[13px]
        lg:static lg:min-h-10 lg:w-auto lg:max-w-none lg:translate-x-0 lg:px-4 lg:shadow-[0_3px_10px_rgba(0,0,0,0.08)]"
      role="timer"
      aria-live="polite"
    >
      <img
        src={timerIconSrc}
        alt=""
        className="h-7 w-7 flex-none object-contain max-[359px]:h-6 max-[359px]:w-6"
        aria-hidden="true"
      />
      <span className="text-[var(--gray-600)]">{t('finalize_at', { context: 'purchase' })}:</span>
      <span className="text-[var(--color-error)]">{timeDisplay}</span>
    </div>
  );
};

QuickPurchaseTimerBadge.propTypes = {
  remainingTime: PropTypes.number,
};

QuickPurchaseTimerBadge.defaultProps = {
  remainingTime: 0,
};

export default QuickPurchaseTimerBadge;
